import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CodeListSection from "components/shared/CodeListSection";
import Api from "api";

const TAB_GROUPS = 0;
const TAB_EXPANDED = 2;
const TAB_CONDENSED = 1;

/**
 * Renders a tab panel that only displays its children when the given tab is active.
 */
const TabPanel = ({ value, index, children }) => {
  if (value !== index) return null;
  return (
    <Box
      role="tabpanel"
      id={`codelist-tabpanel-${index}`}
      aria-labelledby={`codelist-tab-${index}`}
      sx={{ overflowY: "auto", maxHeight: 400 }}
    >
      {children}
    </Box>
  );
};

/**
 * A read-only dialog that fetches code group details for each entry in
 * `diagnosisCodeGroups` and displays consolidated results across three tabs:
 * Diagnosis Code Group(s), Condensed, and Expanded.
 *
 * @param {object} props
 * @param {boolean} props.open - Whether the dialog is open.
 * @param {function} props.onClose - Callback to close the dialog.
 * @param {Array<{codeGroupKey: number, codeGroupType: string}>} [props.diagnosisCodeGroups=[]] - Code group descriptors to fetch.
 */
const CodeListDialog = ({ open, onClose, diagnosisCodeGroups = [] }) => {
  const [tabValue, setTabValue] = useState(TAB_GROUPS);
  const [loading, setLoading] = useState(false);
  const [codeGroupDetails, setCodeGroupDetails] = useState([]);

  /**
   * Reset dialog-local view state before delegating close control to parent.
   */
  const handleClose = () => {
    setCodeGroupDetails([]);
    setTabValue(TAB_GROUPS);
    onClose();
  };
  useEffect(() => {
    if (!open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCodeGroupDetails([]);

      setTabValue(TAB_GROUPS);
      return;
    }
    if (!diagnosisCodeGroups.length) return;

    setLoading(true);
    Promise.all(
      diagnosisCodeGroups.map(({ codeGroupKey, codeGroupType }) =>
        Api.CodeGroup.getCodeGroupDetails(codeGroupKey, codeGroupType).then(
          (res) => res.data
        )
      )
    )
      .then((results) => {
        setCodeGroupDetails(results);
      })
      .catch((error) => {
        console.error("Error fetching diagnosis code group details:", error);
        setCodeGroupDetails([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [open, diagnosisCodeGroups]);

  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
  };

  const consolidatedExpandedCodes = codeGroupDetails.flatMap(
    (detail) => detail?.expandedCodes ?? []
  );

  const consolidatedCondensedCodes = codeGroupDetails.flatMap(
    (detail) => detail?.condensedCodes ?? []
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      data-testid="code-list-dialog"
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Diagnosis Code List</Typography>
          <IconButton
            onClick={handleClose}
            aria-label="close"
            data-testid="code-list-dialog-close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={100}
            data-testid="loading-indicator"
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="fullWidth"
                aria-label="Diagnosis code list tabs"
              >
                <Tab
                  label="Diagnosis Code Group(s)"
                  id="codelist-tab-0"
                  aria-controls="codelist-tabpanel-0"
                  data-testid="tab-diagnosis-code-groups"
                />

                <Tab
                  label="Condensed"
                  value={TAB_CONDENSED}
                  id={`codelist-tab-${TAB_CONDENSED}`}
                  aria-controls={`codelist-tabpanel-${TAB_CONDENSED}`}
                  data-testid="tab-condensed-diagnoses"
                />
                <Tab
                  label="Expanded"
                  value={TAB_EXPANDED}
                  id={`codelist-tab-${TAB_EXPANDED}`}
                  aria-controls={`codelist-tabpanel-${TAB_EXPANDED}`}
                  data-testid="tab-expanded-diagnoses"
                />
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={TAB_GROUPS}>
              {codeGroupDetails.length === 0 ? (
                <Typography sx={{ mt: 2 }} data-testid="groups-empty">
                  No data to display.
                </Typography>
              ) : (
                <List dense data-testid="groups-list">
                  {codeGroupDetails.map((detail, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={`${detail.codeGroupName} v${detail.codeGroupVersion}`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
              <Typography sx={{ mt: 1 }} data-testid="groups-count">
                Total: {codeGroupDetails.length}
              </Typography>
            </TabPanel>

            <TabPanel value={tabValue} index={TAB_EXPANDED}>
              {consolidatedExpandedCodes.length === 0 ? (
                <Typography sx={{ mt: 2 }} data-testid="expanded-empty">
                  No data to display.
                </Typography>
              ) : (
                <CodeListSection
                  selectedCodes={consolidatedExpandedCodes}
                  readOnly
                />
              )}
              <Typography sx={{ mt: 1 }} data-testid="expanded-count">
                Total: {consolidatedExpandedCodes.length}
              </Typography>
            </TabPanel>

            <TabPanel value={tabValue} index={TAB_CONDENSED}>
              {consolidatedCondensedCodes.length === 0 ? (
                <Typography sx={{ mt: 2 }} data-testid="condensed-empty">
                  No data to display.
                </Typography>
              ) : (
                <List dense data-testid="condensed-list">
                  {consolidatedCondensedCodes.map((item, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              )}
              <Typography sx={{ mt: 1 }} data-testid="condensed-count">
                Total: {consolidatedCondensedCodes.length}
              </Typography>
            </TabPanel>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CodeListDialog;
